import { Fragment, ReactNode, useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'

interface Faq { q: string; a: string }

// Wrap any bare http(s) URL in <a>. The raw string is still used for JSON-LD
// (search engines want plain text in FAQ schema), but the visible answer renders
// clickable links — needed for the Supabase docs references in particular.
const URL_PATTERN = /(https?:\/\/[^\s)]+)/g
const TRAILING_PUNCT = /[,.;:!?]+$/
function renderAnswer(text: string): ReactNode {
  // String.split with a capturing group interleaves captures at odd indices.
  const parts = text.split(URL_PATTERN)
  return parts.map((part, i) => {
    if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>
    const trail = part.match(TRAILING_PUNCT)?.[0] ?? ''
    const url = trail ? part.slice(0, -trail.length) : part
    return (
      <Fragment key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
        {trail}
      </Fragment>
    )
  })
}

const FAQS: Faq[] = [
  {
    q: 'What does TokenDance do?',
    a: 'TokenDance is a forward-deployed engineering and LLM infrastructure company. We work with teams to design, build, deploy, and operate production AI systems, including model APIs, structured outputs, evals, agent workflows, GPU inference, and operational runbooks.',
  },
  {
    q: 'What is forward deployed engineering?',
    a: 'Forward deployed engineering means a small senior technical team works inside your business context. We map the workflow, design the AI system, ship the first production path, and transfer the architecture, runbooks, and operating habits back to your team.',
  },
  {
    q: 'What LLM infrastructure do you provide?',
    a: 'We provide the production layer around model-powered products: provider routing, JSON contracts, prompt versioning, eval hooks, retries, logs, rate limits, usage controls, cost visibility, and clean failure envelopes for downstream systems.',
  },
  {
    q: 'Do you build with OpenAI, Anthropic, Gemini, and local models?',
    a: 'Yes. TokenDance can design systems around OpenAI, Anthropic, Google Gemini, xAI, local models, or a mixed-provider gateway. We choose the model path based on latency, quality, data policy, cost, and operational risk.',
  },
  {
    q: 'Can you help with agents, not just chatbots?',
    a: 'Yes. We build agentic workflows that read context, call tools, write outputs, escalate uncertainty, and leave auditable traces. Common use cases include internal research, support operations, sales workflows, document processing, code operations, and analyst copilots.',
  },
  {
    q: 'Do you support GPU inference or fine-tuning infrastructure?',
    a: 'Yes. We help teams package model endpoints, worker queues, batch jobs, retrieval pipelines, and GPU-backed inference services with the resource profile, observability, and runbook needed for production.',
  },
  {
    q: 'What happens in a typical engagement?',
    a: 'We start by scoping the business workflow, data boundary, model risk, and success metric. Then we ship the smallest production slice, harden it with evals and operations, and transfer the system to your team with documentation and support.',
  },
  {
    q: 'What kinds of companies are a good fit?',
    a: 'TokenDance is a good fit for teams with a real operational workflow, proprietary data, or a model-powered product that must survive production conditions. SaaS, enterprise operations, support, research, education, security, and internal tooling teams are common fits.',
  },
  {
    q: 'Do you only advise, or do you write production code?',
    a: 'We write production code. Strategy matters, but the core TokenDance engagement is implementation: APIs, backend services, model integration, eval harnesses, data pipelines, interfaces, deployment paths, and operating documentation.',
  },
  {
    q: 'Can you work with our existing stack?',
    a: 'Usually, yes. We can integrate with your existing app, cloud, data warehouse, vector store, auth provider, observability stack, and model vendors. When a new component is needed, we explain the tradeoff before adding it.',
  },
  {
    q: 'How do you handle security and data boundaries?',
    a: 'We define what data the AI system can access, what gets logged, which model providers can receive payloads, how secrets are handled, where human approvals are required, and how failures are escalated.',
  },
  {
    q: 'How do we talk to TokenDance?',
    a: 'Email hi@tokendance.net with the workflow you want to improve, the systems involved, and the kind of AI capability you want in production. We will respond with the fastest path to scope the work.',
  },
]

const SECTIONS: Array<{ n: string; label: string; body: JSX.Element }> = [
  {
    n: '01',
    label: 'Why FDE',
    body: (
      <>
        AI projects fail when they stay abstract. TokenDance works inside the
        workflow, data boundary, and launch pressure so the system ships as
        production software, not a slide deck.
      </>
    ),
  },
  {
    n: '02',
    label: 'Why LLM infra',
    body: (
      <>
        <em>Models are only one layer.</em> Production AI needs routing, evals,
        structured outputs, observability, cost controls, and a team that knows
        how to keep the loop running.
      </>
    ),
  },
]

const WHY_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
}

export default function Why() {
  useEffect(() => {
    document.body.classList.add('v2-why-page')
    return () => document.body.classList.remove('v2-why-page')
  }, [])

  return (
    <>
      <SEO
        title="Why TokenDance"
        description="TokenDance is a forward-deployed engineering and LLM infrastructure company for teams shipping production AI systems."
        path="/why"
        jsonLd={WHY_JSONLD}
      />

      <div className="od-home od-why-shell">
        <SiteNav />

        <main className="od-why-main">
          <section className="od-why-hero" aria-labelledby="why-title">
            <h1 id="why-title">
              FDE teams and LLM infrastructure for production AI.
            </h1>
            <p>
              TokenDance helps businesses turn ambiguous AI opportunities into
              deployed systems with model APIs, eval loops, agents, GPU paths,
              and operating runbooks.
            </p>
          </section>

          <section className="od-why-grid" aria-label="Why TokenDance">
            {SECTIONS.map((s) => (
              <article key={s.n} className="od-why-card">
                <div className="od-why-card-head">
                  <span className="od-why-card-n">{s.n}</span>
                  <span>{s.label}</span>
                </div>
                <p>{s.body}</p>
              </article>
            ))}
          </section>

          <section className="od-why-stats" aria-labelledby="why-stats-title">
            <header className="od-why-stats-head">
              <h2 id="why-stats-title">The DevOps tax, in real numbers.</h2>
              <p>
                Public surveys. Real sources. The gap between AI prototypes and
                production systems is now an operations problem.
              </p>
            </header>

            <ol className="od-why-stats-grid">
              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Teams that ship a change in under 1 hour
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">industry</span>
                    <span className="od-why-stat-num">9.4%</span>
                    <span className="od-why-stat-side">
                      only this many teams complete a code-to-prod change
                      in &lt; 1 hour (DORA 2025)
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">TokenDance</span>
                    <span className="od-why-stat-num">FDE</span>
                    <span className="od-why-stat-side">
                      embedded builders shorten the path from business workflow
                      to shipped AI system
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://dora.dev/research/2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    DORA 2025 Accelerate State of DevOps Report
                  </a>
                </p>
              </li>

              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Deploy success rate (1 − change failure rate)
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">industry</span>
                    <span className="od-why-stat-num">8.5%</span>
                    <span className="od-why-stat-side">
                      of teams hit the ideal 0–2% change failure rate
                      (DORA 2025); elite cluster ≈ 5% CFR
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">TokenDance · target</span>
                    <span className="od-why-stat-num">≥ 95%</span>
                    <span className="od-why-stat-side">
                      production AI needs evals, rollback paths, and clear
                      failure envelopes before launch
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://dora.dev/research/2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    DORA 2025 Accelerate State of DevOps Report
                  </a>
                </p>
              </li>

              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Devs frustrated by tech debt &amp; deploy-stack complexity
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">human</span>
                    <span className="od-why-stat-num">62%</span>
                    <span className="od-why-stat-side">
                      flag tech debt as a top frustration; complex build /
                      deploy stacks rank #2–#3 (Stack Overflow 2025, 49k+ devs)
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">TokenDance</span>
                    <span className="od-why-stat-num">done with you</span>
                    <span className="od-why-stat-side">
                      we absorb the AI infrastructure complexity and transfer a
                      maintainable system back to your team
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://survey.stackoverflow.co/2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    2025 Stack Overflow Developer Survey
                  </a>
                </p>
              </li>

              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Productivity lift when AI runs the outer loop
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">github · 2025</span>
                    <span className="od-why-stat-num">+55%</span>
                    <span className="od-why-stat-side">
                      AI tools lift developer productivity; manual deploys
                      &ldquo;more and more rare&rdquo; (Octoverse 2025)
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">TokenDance</span>
                    <span className="od-why-stat-num">AI ops</span>
                    <span className="od-why-stat-side">
                      model APIs, agents, evals, logs, and runbooks packaged
                      into one production operating loop
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://github.blog/news-insights/octoverse/what-986-million-code-pushes-say-about-the-developer-workflow-in-2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub Octoverse 2025 — Developer Workflow Report
                  </a>
                </p>
              </li>
            </ol>
          </section>

          <section className="od-why-faq" aria-labelledby="why-faq-title">
            <h2 id="why-faq-title">Frequently asked</h2>
            <div className="od-why-faq-list">
              {FAQS.map((f) => (
                <details key={f.q} className="od-why-faq-item">
                  <summary>{f.q}</summary>
                  <p>{renderAnswer(f.a)}</p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  )
}
